import { isItemActive } from '@server/documentation';
import type {
  SalesforceItem,
  SalesforceItemField,
  SalesforceItemRollup,
} from '../parser/ParserTypes';
import {
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_AUTOMATION,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
  SWEEP_SCHEDULED_ASSIGNMENT,
  ROLLUP,
} from '../parser/ParserTypes';
import { ConfigurationToName, UnclassifiedTypes } from './salesforce-documentation-card/types';
import { getDataFromSFFieldName } from '../pages/rollups/rollupHelpers';
import { colors } from '@sweep-io/sweep-design';
import type { TagDto } from '../../types/documentationUserInputs';
import { telemetry } from '../../telemetry';
import { getObjectTypeColor } from '../sweep-canvas/helpers/getObjectTypeColor';
import { FunnelFieldLabels } from '../../constants/deprecated_fieldsManagementConsts';
import { formatCamelCase } from './universal-search/utils';
import { FIELD } from '@server/parser';
import type { SweepFieldSummary } from '../../types/fields';
import { AutomationType } from '../../types/enums/AutomationType';
import type { RollupDto } from '../pages/rollups/rollupTypes';

export const getObjectApiNamesFromFunnelMap = (
  funnelsData?: FunnelsData,
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  Object.values(funnelsData ?? {}).forEach((funnel) => {
    const { crmOrgId } = funnel;
    const objectName = funnel.recordType?.objectName;
    if (!crmOrgId || !objectName) return;
    if (!result[crmOrgId]) result[crmOrgId] = [];
    if (!result[crmOrgId].includes(objectName)) result[crmOrgId].push(objectName);
  });

  return result;
};

export const displayCorrectDateFormat = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW = '250px';
export const OCCUPIED_SPACE_MAIN_VIEW = '200px';
export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW_CANVAS = '300px';

export const mapMissingInternalIdToMetadataId = (item: any) => ({
  ...item,
  id: item.id === 'MissingInternalId' || !item.id ? item.metadataId : item.id,
});

export const checkIfActive = (onlyActive: boolean, item: any) =>
  !onlyActive || (onlyActive && isItemActive(item));

export const translateTypeToHumanName = (type: string) => {
  if (!type) {
    return type;
  }
  return (
    ConfigurationToName[type] ?? FunnelFieldLabels[type as SweepFieldTypes] ?? formatCamelCase(type)
  );
};

export const automationTypeToConfigurationType = (type: AutomationType) => {
  switch (type) {
    case AutomationType.Alert:
      return SWEEP_ALERT;
    case AutomationType.Assignment:
      return SWEEP_ASSIGNMENT;
    case AutomationType.Dedupe:
      return SWEEP_DEDUPE;
    case AutomationType.Matching:
      return SWEEP_MATCHING;
    case AutomationType.ScheduledAssignment:
      return SWEEP_SCHEDULED_ASSIGNMENT;
    case AutomationType.Default:
      return SWEEP_AUTOMATION;

    case AutomationType.ScheduledReport:
    case AutomationType.ScheduledJob:
    case AutomationType.MarketingAttributionModel:
      //no documentation support yet
      break;

    default:
      telemetry.captureError(new Error('unknown type in automationTypeToConfigurationType'), {
        type,
      });
      break;
  }
};


export const createConfigurationItemFromTag = (tag: TagDto): SalesforceItem => ({
  id: tag.id,
  name: tag.name,
  label: tag.name,

  objectApiNames: [],
  type: UnclassifiedTypes.tag,
  isActive: true,
  parentType: UnclassifiedTypes.tag,
  metadataId: '',
});

const buildFieldMetadataId = (objectName: string, sfFieldName: string) =>
  `salesforce.${objectName}.field.${sfFieldName}`;

export const createConfigurationItemFromSweepField = (
  sweepField: SweepFieldSummary,
): SalesforceItemField => ({
  id: sweepField.id ?? '',
  name: getDataFromSFFieldName(sweepField?.sfFieldName)?.[1] ?? '',
  label: sweepField.sweepFieldName ?? '',

  objectApiNames: [sweepField.objectName],
  type: sweepField.fieldType,
  fieldType: sweepField.fieldType,
  isActive: true,
  parentType: FIELD,
  metadataId: buildFieldMetadataId(sweepField.objectName, sweepField.sfField?.name ?? ''),
});

export const createConfigurationItemFromRollupField = (
  rollup: RollupDto,
  { usage, link }: { usage?: number; link?: string } = {},
): SalesforceItemRollup => ({
  id: rollup.rollupId,
  name: getDataFromSFFieldName(rollup.rollupField?.sfFieldName)?.[1] ?? '',
  label: rollup.rollupField.sweepFieldName ?? '',
  objectApiNames: [rollup.objectName],
  type: rollup.rollupField.fieldType,
  description: rollup.rollupField.description,
  createdAt: displayCorrectDateFormat(rollup.createdAt),
  createdBy: rollup.createdById ?? '',
  updatedAt: displayCorrectDateFormat(rollup.updatedAt),
  updatedBy: rollup.updatedById ?? '',
  helpText: rollup.rollupField.helpText,
  isMandatory: rollup.rollupField.isRequired,
  formula: (rollup.rollupField.properties as { formula?: string } | undefined)?.formula,
  usage,
  link,
  isActive: rollup.isActive,
  isRollup: true,
  parentType: ROLLUP,
  metadataId: rollup.rollupId,
  fieldType: rollup.rollupField.fieldType,
});


export const getObjectNameColor = (objectName: string) => {
  const leadingColor = getObjectTypeColor(objectName)?.leadingColorName as keyof typeof colors;
  return (colors[leadingColor] as any)?.[100] ?? colors['blue'][100];
};

export const getConfigurationItemColor = (
  configurationItem: SalesforceItem,
  canvasColor?: string,
  tags?: TagDto[],
): string => {
  // Canvas color takes precedence (but ignore white as it's the default)
  if (canvasColor && canvasColor.toLowerCase() !== '#ffffff') {
    return canvasColor;
  }

  // For tags, use the tag's own color
  if (configurationItem.parentType === UnclassifiedTypes.tag && tags) {
    const tag = tags.find((t) => t.id === configurationItem.id);
    if (tag?.color) {
      return (
        (colors[tag.color as keyof typeof colors] as any)?.[100] ?? tag.color ?? colors.blue[100]
      );
    }
  }

  // Default: derive from object name
  const objectName = configurationItem.objectApiNames?.[0];
  return getObjectNameColor(objectName) ?? colors.blue[100];
};
